import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faSpinner,
  faTrash,
  faExchangeAlt,
  faChartLine,
  faCube,
  faBrain
} from '@fortawesome/free-solid-svg-icons';
import { getApiBaseUrl } from '../../services/api';
import './ModelVersionSwitcher.css';

interface ModelVersion {
  id: string;
  name: string;
  type: 'base' | 'fine_tuned';
  is_active: boolean;
  created_at: string | null;
  training_examples: number;
  job_id?: string;
  usage_stats: {
    total_requests: number;
    total_tokens: number;
    avg_response_time: number;
  };
}

interface ModelsData {
  active_model: string;
  models: ModelVersion[];
}

interface ModelVersionSwitcherProps {
  clientId: string;
  onModelChange?: () => void;
}

const ModelVersionSwitcher: React.FC<ModelVersionSwitcherProps> = ({
  clientId,
  onModelChange
}) => {
  const [modelsData, setModelsData] = useState<ModelsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Confirmation modals
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelVersion | null>(null);

  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadModels = async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/fine-tuning/${clientId}/models`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to load model versions');

      const data = await response.json();
      setModelsData(data);
    } catch (error: any) {
      console.error('Error loading models:', error);
      toast.error(error.message || 'Failed to load model versions');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchModel = async () => {
    if (!selectedModel) return;

    try {
      setActionLoading('switch');
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/fine-tuning/${clientId}/models/switch`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ model_id: selectedModel.id })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to switch model');
      }

      const result = await response.json();
      toast.success(result.message || 'Model switched successfully');

      setShowSwitchModal(false);
      setSelectedModel(null);
      loadModels();
      if (onModelChange) onModelChange();
    } catch (error: any) {
      console.error('Error switching model:', error);
      toast.error(error.message || 'Failed to switch model');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteModel = async () => {
    if (!selectedModel || selectedModel.type === 'base') return;

    try {
      setActionLoading('delete');
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');

      const response = await fetch(
        `${getApiBaseUrl()}/fine-tuning/${clientId}/models/${selectedModel.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete model');
      }

      toast.success('Model version deleted successfully');

      setShowDeleteModal(false);
      setSelectedModel(null);
      loadModels();
      if (onModelChange) onModelChange();
    } catch (error: any) {
      console.error('Error deleting model:', error);
      toast.error(error.message || 'Failed to delete model');
    } finally {
      setActionLoading(null);
    }
  };

  const openSwitchModal = (model: ModelVersion) => {
    setSelectedModel(model);
    setShowSwitchModal(true);
  };

  const openDeleteModal = (model: ModelVersion) => {
    setSelectedModel(model);
    setShowDeleteModal(true);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="model-switcher-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading model versions...</p>
      </div>
    );
  }

  if (!modelsData) {
    return (
      <div className="model-switcher-error">
        <p>Failed to load model versions</p>
      </div>
    );
  }

  const activeModel = modelsData.models.find((m) => m.is_active);
  const allModels = modelsData.models;

  return (
    <div className="model-version-switcher">
      <div className="switcher-header">
        <h2>Model Versions</h2>
        <p className="switcher-subtitle">
          Manage and switch between different model versions
        </p>
      </div>

      {/* Active Model - Compact Card */}
      {activeModel && (
        <div className="active-model-compact">
          <div className="compact-header">
            <div className="compact-title">
              <FontAwesomeIcon
                icon={activeModel.type === 'base' ? faCube : faBrain}
                className="compact-icon"
              />
              <span>Currently Active</span>
            </div>
            <div className="active-badge-small">
              <FontAwesomeIcon icon={faCheckCircle} />
              Active
            </div>
          </div>

          <div className="compact-content">
            <div className="compact-row">
              <span className="compact-label">Model:</span>
              <code className="compact-code">{activeModel.name}</code>
            </div>

            {activeModel.type === 'fine_tuned' && (
              <>
                <div className="compact-row">
                  <span className="compact-label">Training Examples:</span>
                  <strong>{activeModel.training_examples}</strong>
                </div>
                <div className="compact-row">
                  <span className="compact-label">Usage:</span>
                  <span>{formatNumber(activeModel.usage_stats.total_requests)} requests, {formatNumber(activeModel.usage_stats.total_tokens)} tokens</span>
                </div>
              </>
            )}

            {activeModel.type === 'base' && (
              <div className="compact-row">
                <span className="compact-label">Type:</span>
                <span>Default OpenAI Model</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Models */}
      {allModels.length > 0 && (
        <div className="all-models-section">
          <h3 className="section-title">All Available Versions</h3>
          <div className="models-grid">
            {allModels.map((model) => {
              const isActive = model.is_active;

              return (
                <div
                  key={model.id}
                  className={`model-card ${isActive ? 'active-card' : ''}`}
                >
                  <div className="card-header">
                    <FontAwesomeIcon
                      icon={model.type === 'base' ? faCube : faBrain}
                      className="model-icon"
                    />
                    <span className="model-type-badge">
                      {model.type === 'base' ? 'Base Model' : 'Fine-Tuned Model'}
                    </span>
                    {isActive && (
                      <span className="active-indicator">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Active
                      </span>
                    )}
                  </div>

                  <div className="model-info-compact">
                    <code className="model-name-compact">{model.name}</code>

                    {model.type === 'fine_tuned' && (
                      <div className="model-meta">
                        <span>{model.training_examples} examples</span>
                        <span>•</span>
                        <span>
                          {model.created_at
                            ? new Date(model.created_at).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                    )}

                    {model.type === 'base' && (
                      <div className="model-meta">
                        <span>Default OpenAI Model</span>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    {!isActive ? (
                      <>
                        <button
                          className={`btn-switch ${model.type === 'base' ? 'btn-switch-default' : ''}`}
                          onClick={() => openSwitchModal(model)}
                        >
                          <FontAwesomeIcon icon={faExchangeAlt} />
                          {model.type === 'base' ? 'Use Default' : 'Activate'}
                        </button>

                        {model.type === 'fine_tuned' && (
                          <button
                            className="btn-delete-icon"
                            onClick={() => openDeleteModal(model)}
                            title="Delete this version"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="current-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Current Model
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Switch Confirmation Modal */}
      {showSwitchModal && selectedModel && (
        <div className="modal-overlay" onClick={() => setShowSwitchModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {selectedModel.type === 'base' ? 'Switch to Default Model' : 'Switch Active Model'}
            </h3>
            <p>
              {selectedModel.type === 'base' ? (
                <>
                  You are about to switch to the <strong>Default Base Model</strong>.
                  This is a quick way to rollback if fine-tuned responses aren't working as expected.
                </>
              ) : (
                <>
                  You are about to switch to <strong>Fine-Tuned Model</strong>
                </>
              )}
            </p>

            <div className="model-preview">
              <code>{selectedModel.name}</code>
              {selectedModel.type === 'fine_tuned' && (
                <div className="preview-meta">
                  <span>{selectedModel.training_examples} training examples</span>
                </div>
              )}
              {selectedModel.type === 'base' && (
                <div className="preview-meta">
                  <span>OpenAI's standard model without custom training</span>
                </div>
              )}
            </div>

            <div className="modal-warning">
              <strong>Note:</strong> This will immediately affect all chat responses for
              this client.
              {selectedModel.type === 'base' &&
                ' Your custom training will be preserved and you can re-activate it anytime.'}
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowSwitchModal(false)}
                disabled={actionLoading === 'switch'}
              >
                Cancel
              </button>
              <button
                className={`btn-primary ${selectedModel.type === 'base' ? 'btn-switch-default' : ''}`}
                onClick={handleSwitchModel}
                disabled={actionLoading === 'switch'}
              >
                {actionLoading === 'switch' ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Switching...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    {selectedModel.type === 'base' ? 'Use Default Model' : 'Switch Model'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedModel && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Model Version</h3>
            <p>
              You are about to delete this fine-tuned model version. This action cannot
              be undone.
            </p>

            <div className="model-preview">
              <code>{selectedModel.name}</code>
              <div className="preview-meta">
                <span>{selectedModel.training_examples} training examples</span>
                <span>•</span>
                <span>
                  {selectedModel.created_at
                    ? new Date(selectedModel.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
            </div>

            <div className="modal-warning danger">
              <strong>Warning:</strong> This will permanently remove this model version
              from your dashboard. The model data in OpenAI will not be deleted.
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={actionLoading === 'delete'}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteModel}
                disabled={actionLoading === 'delete'}
              >
                {actionLoading === 'delete' ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelVersionSwitcher;
